import React from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { PieChart } from "@mui/x-charts/PieChart";

/**
 * Expanded breakdown for a CompactMetricCard, opened on click instead of the old hover tooltip —
 * a bigger donut plus a full label/value/percentage list, reusable across any page's metric cards.
 *
 * Props:
 *   open, onClose, title — same title shown on the card
 *   data      — [{ label, value }]
 *   colorMap  — { [label]: hexColor }
 */
export const MetricDashboardDialog = ({ open, onClose, title, data, colorMap = {} }) => {
  const rows = Array.isArray(data) ? data : [];
  const total = rows.reduce((sum, item) => sum + (item.value || 0), 0);
  const chartData = rows.map((item, index) => ({
    id: index,
    value: item.value,
    label: item.label,
    color: colorMap[item.label] || "#c7c7c7",
  }));

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {title}
        <IconButton aria-label="close" onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {rows.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: "text.secondary", py: 3, textAlign: "center" }}>
            No data available.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Box sx={{ position: "relative", width: 180, height: 180 }}>
              <PieChart
                series={[{ data: chartData, innerRadius: 56, outerRadius: 88, paddingAngle: 2, cornerRadius: 3 }]}
                width={180}
                height={180}
                slots={{ legend: () => null }}
                slotProps={{ legend: { hidden: true } }}
                margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
              />
              <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                <Typography sx={{ fontSize: 26, fontWeight: 700, color: "#2c2a1e", lineHeight: 1 }}>{total}</Typography>
                <Typography sx={{ fontSize: 11, color: "#6b6a52" }}>total</Typography>
              </Box>
            </Box>

            <Box sx={{ width: "100%", mt: 3 }}>
              {rows
                .slice()
                .sort((a, b) => (b.value || 0) - (a.value || 0))
                .map((item) => {
                  const pct = total > 0 ? Math.round((item.value / total) * 100) : 0;
                  return (
                    <Box
                      key={item.label}
                      sx={{
                        display: "flex", alignItems: "center", gap: 1.25, py: 0.75,
                        borderBottom: "1px solid #F0F0F0",
                        "&:last-child": { borderBottom: "none" },
                      }}
                    >
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: colorMap[item.label] || "#c7c7c7", flexShrink: 0 }} />
                      <Typography sx={{ fontSize: 13, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {item.label}
                      </Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary", whiteSpace: "nowrap" }}>
                        {item.value} ({pct}%)
                      </Typography>
                    </Box>
                  );
                })}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="primary">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MetricDashboardDialog;
